import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocListItem,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { joinSeries, tidy } from "@/lib/tool-kit/essay/sentence";
import {
  countWords,
  leadInItem,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";
import {
  isFilled,
  itemOrPrompt,
  lettered,
  listItemOrPrompt,
} from "@/lib/tool-kit/lit-essays/common";

/**
 * A debate constructive speech: resolution, definitions, a framework, contentions in
 * claim–warrant–evidence–impact order, prepared rebuttal blocks, and the voting issues.
 *
 * The shape is the one most school debate formats share. The formats differ in what
 * sits above the contentions: Lincoln–Douglas has a value and a value criterion;
 * policy debate has a plan (affirmative) or a position (negative); Public Forum and
 * classroom debates may open with a short framework or none at all. The builder keeps
 * one structure and changes the label and prompt of the framework slot.
 *
 * Speech lengths are the usual first-speech times — Public Forum 4 minutes,
 * Lincoln–Douglas affirmative 6, policy first affirmative 8 — and are only defaults:
 * leagues and classrooms differ, so the length is an input.
 */

export type DebateFormat = "pf" | "ld" | "policy" | "classroom";
export type DebateSide = "aff" | "neg";

export const DEBATE_FORMATS: readonly DebateFormat[] = [
  "pf",
  "ld",
  "policy",
  "classroom",
];
export const DEBATE_SIDES: readonly DebateSide[] = ["aff", "neg"];

/** Typical constructive-speech length, in minutes, by format. */
export const DEFAULT_MINUTES: Readonly<Record<DebateFormat, number>> = {
  pf: 4,
  ld: 6,
  policy: 8,
  classroom: 3,
};

/** Words per minute for a clear delivery; competitive speaking is faster. */
export const DEFAULT_WPM = 150;

export interface Contention {
  id: number;
  /** A short tagline, the label judges flow: "Sleep health". */
  tagline: string;
  claim: string;
  /** Why the claim is true — the reasoning, before the evidence. */
  warrant: string;
  /** Evidence, one cited item per line. */
  evidence: string;
  /** What follows: magnitude, probability, timeframe. */
  impact: string;
}

export interface RebuttalBlock {
  id: number;
  theySay: string;
  weRespond: string;
  because: string;
  impact: string;
}

export interface DebateInput {
  format: DebateFormat;
  side: DebateSide;
  resolution: string;
  /** One definition per line: "term — meaning". */
  definitions: string;
  /** Value, plan text, or framework — whichever the format asks for. */
  frameworkMain: string;
  /** The value criterion; used only by Lincoln–Douglas. */
  frameworkCriterion: string;
  contentions: readonly Contention[];
  rebuttals: readonly RebuttalBlock[];
  /** The voting issues, one per line. */
  voters: string;
}

export const MIN_CONTENTIONS = 1;
export const MAX_CONTENTIONS = 4;
export const MAX_REBUTTALS = 4;

export function blankContention(id: number): Contention {
  return { id, tagline: "", claim: "", warrant: "", evidence: "", impact: "" };
}

export function blankRebuttal(id: number): RebuttalBlock {
  return { id, theySay: "", weRespond: "", because: "", impact: "" };
}

/* ------------------------------------------------------------------ timing */

export type TimeState = "empty" | "under" | "ok" | "over";

export interface SpeechTiming {
  words: number;
  /** Estimated delivery time at the given pace, in seconds. */
  seconds: number;
  budgetSeconds: number;
  /** How many words the time budget holds at this pace. */
  capacity: number;
  state: TimeState;
}

/** Words the student has written, across every field that will be spoken. */
export function caseWords(input: DebateInput): number {
  const parts: string[] = [
    input.frameworkMain,
    input.frameworkCriterion,
    input.voters,
    ...splitItems(input.definitions),
    ...input.contentions.flatMap((c) => [
      c.tagline,
      c.claim,
      c.warrant,
      c.evidence,
      c.impact,
    ]),
    ...input.rebuttals.flatMap((r) => [
      r.theySay,
      r.weRespond,
      r.because,
      r.impact,
    ]),
  ];
  return parts.reduce((total, part) => total + countWords(part), 0);
}

/**
 * Delivery time at `wpm` words a minute against `minutes`. "Under" is less than 80% of
 * the budget, which usually means there is room for more evidence or weighing.
 */
export function speechTiming(
  words: number,
  minutes: number,
  wpm: number
): SpeechTiming {
  const pace = wpm > 0 ? wpm : DEFAULT_WPM;
  const budgetSeconds = Math.max(0, minutes) * 60;
  const seconds = Math.round((words / pace) * 60);
  const capacity = Math.floor((budgetSeconds / 60) * pace);
  let state: TimeState = "ok";
  if (words === 0) {
    state = "empty";
  } else if (seconds > budgetSeconds) {
    state = "over";
  } else if (seconds < budgetSeconds * 0.8) {
    state = "under";
  }
  return { words, seconds, budgetSeconds, capacity, state };
}

/** 125 seconds → "2:05". */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = String(whole % 60).padStart(2, "0");
  return `${minutes}:${rest}`;
}

/* ------------------------------------------------------------------ document */

export interface DebateLabels {
  docTitle: Readonly<Record<DebateSide, string>>;
  subtitle: string;
  formats: Readonly<Record<DebateFormat, string>>;
  headings: {
    resolution: string;
    introduction: string;
    definitions: string;
    contention: string;
    contentionBlank: string;
    rebuttals: string;
    block: string;
    conclusion: string;
  };
  items: {
    roadmap: string;
    claim: string;
    warrant: string;
    evidence: string;
    impact: string;
    theySay: string;
    weRespond: string;
    because: string;
    therefore: string;
    voters: string;
    value: string;
    criterion: string;
  };
  framework: Readonly<
    Record<DebateFormat, { heading: string; prompt: string }>
  >;
  prompts: {
    resolution: string;
    definitions: string;
    roadmap: string;
    claim: string;
    warrant: string;
    evidence: string;
    impact: string;
    rebuttal: string;
    voters: string;
    criterion: string;
  };
  guidance: { contention: string; rebuttals: string; definitions: string };
  roadmapLine: string;
  contentionStand: string;
  and: string;
}

function resolutionSection(
  input: DebateInput,
  labels: DebateLabels
): DocSection {
  return {
    heading: labels.headings.resolution,
    blocks: [
      isFilled(input.resolution)
        ? { kind: "paragraph", text: tidy(input.resolution) }
        : { kind: "prompt", text: labels.prompts.resolution },
    ],
  };
}

function roadmapItem(input: DebateInput, labels: DebateLabels): DocListItem {
  const names = input.contentions.map((contention, index) =>
    isFilled(contention.tagline)
      ? tidy(contention.tagline)
      : t(labels.contentionStand, { n: index + 1 })
  );
  const anyNamed = input.contentions.some((c) => isFilled(c.tagline));
  if (!anyNamed) {
    return { text: labels.prompts.roadmap, prompt: true };
  }
  return {
    text: [
      { text: `${labels.items.roadmap}:`, bold: true },
      {
        text: ` ${t(labels.roadmapLine, { list: joinSeries(names, labels.and) })}`,
      },
    ],
  };
}

function frameworkItems(
  input: DebateInput,
  labels: DebateLabels
): DocListItem[] {
  const copy = labels.framework[input.format];
  if (input.format === "ld") {
    return [
      itemOrPrompt(labels.items.value, tidy(input.frameworkMain), copy.prompt),
      itemOrPrompt(
        labels.items.criterion,
        tidy(input.frameworkCriterion),
        labels.prompts.criterion
      ),
    ];
  }
  return [itemOrPrompt(copy.heading, tidy(input.frameworkMain), copy.prompt)];
}

function introductionSection(
  input: DebateInput,
  labels: DebateLabels
): DocSection {
  return {
    heading: labels.headings.introduction,
    blocks: [
      lettered([roadmapItem(input, labels), ...frameworkItems(input, labels)]),
    ],
  };
}

function definitionsSection(
  input: DebateInput,
  labels: DebateLabels
): DocSection {
  const lines = splitItems(input.definitions);
  const blocks: DocBlock[] = [
    { kind: "guidance", text: labels.guidance.definitions },
  ];
  blocks.push(
    lines.length === 0
      ? { kind: "prompt", text: labels.prompts.definitions }
      : {
          kind: "list",
          list: {
            style: "bullet",
            items: lines.map((line) => ({ text: leadInItem(line) })),
          },
        }
  );
  return { heading: labels.headings.definitions, blocks };
}

function contentionSection(
  contention: Contention,
  index: number,
  labels: DebateLabels
): DocSection {
  const heading = isFilled(contention.tagline)
    ? t(labels.headings.contention, {
        n: index + 1,
        tagline: tidy(contention.tagline),
      })
    : t(labels.headings.contentionBlank, { n: index + 1 });
  return {
    heading,
    blocks: [
      ...(index === 0
        ? [{ kind: "guidance", text: labels.guidance.contention } as DocBlock]
        : []),
      lettered([
        itemOrPrompt(
          labels.items.claim,
          tidy(contention.claim),
          labels.prompts.claim
        ),
        itemOrPrompt(
          labels.items.warrant,
          tidy(contention.warrant),
          labels.prompts.warrant
        ),
        listItemOrPrompt(
          labels.items.evidence,
          contention.evidence,
          labels.prompts.evidence
        ),
        itemOrPrompt(
          labels.items.impact,
          tidy(contention.impact),
          labels.prompts.impact
        ),
      ]),
    ],
  };
}

function rebuttalSection(
  input: DebateInput,
  labels: DebateLabels
): DocSection | null {
  if (input.rebuttals.length === 0) {
    return null;
  }
  const blocks: DocBlock[] = [
    { kind: "guidance", text: labels.guidance.rebuttals },
  ];
  input.rebuttals.forEach((block, index) => {
    blocks.push({
      kind: "subheading",
      text: t(labels.headings.block, { n: index + 1 }),
    });
    blocks.push({
      kind: "list",
      list: {
        style: "bullet",
        items: [
          itemOrPrompt(
            labels.items.theySay,
            tidy(block.theySay),
            labels.prompts.rebuttal
          ),
          itemOrPrompt(
            labels.items.weRespond,
            tidy(block.weRespond),
            labels.prompts.rebuttal
          ),
          itemOrPrompt(
            labels.items.because,
            tidy(block.because),
            labels.prompts.rebuttal
          ),
          itemOrPrompt(
            labels.items.therefore,
            tidy(block.impact),
            labels.prompts.rebuttal
          ),
        ],
      },
    });
  });
  return { heading: labels.headings.rebuttals, blocks };
}

function conclusionSection(
  input: DebateInput,
  labels: DebateLabels
): DocSection {
  const voters = splitItems(input.voters);
  return {
    heading: labels.headings.conclusion,
    blocks:
      voters.length === 0
        ? [{ kind: "prompt", text: labels.prompts.voters }]
        : [
            {
              kind: "list",
              list: {
                style: "decimal",
                items: voters.map((voter) => ({ text: voter })),
              },
            },
          ],
  };
}

/** The constructive speech as a document. */
export function buildDebateCase(
  input: DebateInput,
  labels: DebateLabels
): DocumentModel {
  const sections: DocSection[] = [
    resolutionSection(input, labels),
    introductionSection(input, labels),
    definitionsSection(input, labels),
    ...input.contentions.map((contention, index) =>
      contentionSection(contention, index, labels)
    ),
  ];
  const rebuttals = rebuttalSection(input, labels);
  if (rebuttals) {
    sections.push(rebuttals);
  }
  sections.push(conclusionSection(input, labels));
  return {
    title: labels.docTitle[input.side],
    subtitle: t(labels.subtitle, { format: labels.formats[input.format] }),
    sections,
  };
}
